import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios, { type InternalAxiosRequestConfig } from "axios";

type ApiErrorContextValue = {
  hasApiError: boolean;
  resetApiError: () => void;
};

type PausedError = Error & { __apiErrorPaused?: boolean };
type NavTagged<T> = T & { __apiErrorNavId?: number };
type ApiEventDetail = { navId?: number };
type PatchedWindow = typeof window & {
  __apiErrorFetchPatched?: boolean;
  __apiErrorAxiosPatched?: boolean;
  __apiErrorHistoryPatched?: boolean;
};

const ApiErrorContext = createContext<ApiErrorContextValue | undefined>(undefined);

const API_ERROR_EVENT = "api-error";
const API_SUCCESS_EVENT = "api-success";
const API_ROUTE_CHANGE_EVENT = "api-route-change";

const createPausedError = (): PausedError => {
  const error: PausedError = new Error("API requests paused due to previous failure.");
  error.__apiErrorPaused = true;
  return error;
};

const dispatchApiEvent = (name: string, navId?: number) => {
  window.dispatchEvent(new CustomEvent<ApiEventDetail>(name, { detail: { navId } }));
};

const dispatchRouteChange = () => {
  window.dispatchEvent(new CustomEvent(API_ROUTE_CHANGE_EVENT));
};

const readNavId = (event: Event) =>
  ((event as CustomEvent<ApiEventDetail>).detail || {}).navId;

export const ApiErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasApiError, setHasApiError] = useState(false);
  const navIdRef = useRef(0);
  const hasApiErrorRef = useRef(false);
  const hasSuccessForNavRef = useRef(false);

  const resetApiError = useCallback(() => {
    setHasApiError(false);
  }, []);

  useEffect(() => {
    hasApiErrorRef.current = hasApiError;
  }, [hasApiError]);

  useEffect(() => {
    const handleApiError = (event: Event) => {
      if (readNavId(event) !== navIdRef.current) {
        return;
      }
      if (hasSuccessForNavRef.current) {
        return;
      }
      setHasApiError(true);
    };

    const handleApiSuccess = (event: Event) => {
      if (readNavId(event) !== navIdRef.current) {
        return;
      }
      hasSuccessForNavRef.current = true;
      setHasApiError(false);
    };

    const handleRouteChange = () => {
      navIdRef.current += 1;
      hasSuccessForNavRef.current = false;
      setHasApiError(false);
    };

    window.addEventListener(API_ERROR_EVENT, handleApiError);
    window.addEventListener(API_SUCCESS_EVENT, handleApiSuccess);
    window.addEventListener(API_ROUTE_CHANGE_EVENT, handleRouteChange);

    return () => {
      window.removeEventListener(API_ERROR_EVENT, handleApiError);
      window.removeEventListener(API_SUCCESS_EVENT, handleApiSuccess);
      window.removeEventListener(API_ROUTE_CHANGE_EVENT, handleRouteChange);
    };
  }, []);

  useEffect(() => {
    const win: PatchedWindow = window;

    if (!win.__apiErrorFetchPatched) {
      const originalFetch = window.fetch.bind(window);
      window.fetch = async (...args) => {
        if (hasApiErrorRef.current) {
          return Promise.reject(createPausedError());
        }
        const navId = navIdRef.current;
        try {
          const response = await originalFetch(...args);
          if (!response.ok && response.status >= 500) {
            dispatchApiEvent(API_ERROR_EVENT, navId);
          } else if (response.ok) {
            dispatchApiEvent(API_SUCCESS_EVENT, navId);
          }
          return response;
        } catch (error) {
          dispatchApiEvent(API_ERROR_EVENT, navId);
          throw error;
        }
      };
      win.__apiErrorFetchPatched = true;
    }

    if (!win.__apiErrorAxiosPatched) {
      axios.interceptors.request.use(config => {
        if (hasApiErrorRef.current) {
          return Promise.reject(createPausedError());
        }
        (config as NavTagged<InternalAxiosRequestConfig>).__apiErrorNavId = navIdRef.current;
        return config;
      });

      axios.interceptors.response.use(
        response => {
          dispatchApiEvent(
            API_SUCCESS_EVENT,
            (response.config as NavTagged<InternalAxiosRequestConfig>).__apiErrorNavId
          );
          return response;
        },
        (error: unknown) => {
          if ((error as PausedError | undefined)?.__apiErrorPaused) {
            return Promise.reject(error);
          }
          const axiosError = axios.isAxiosError(error) ? error : undefined;
          const status = axiosError?.response?.status;
          const navId =
            (axiosError?.config as NavTagged<InternalAxiosRequestConfig> | undefined)
              ?.__apiErrorNavId ?? navIdRef.current;
          if (!status || status >= 500) {
            dispatchApiEvent(API_ERROR_EVENT, navId);
          }
          return Promise.reject(error);
        }
      );
      win.__apiErrorAxiosPatched = true;
    }

    if (!win.__apiErrorHistoryPatched) {
      const originalPushState = window.history.pushState.bind(window.history);
      const originalReplaceState = window.history.replaceState.bind(window.history);

      window.history.pushState = (...args) => {
        const result = originalPushState(...args);
        dispatchRouteChange();
        return result;
      };

      window.history.replaceState = (...args) => {
        const result = originalReplaceState(...args);
        dispatchRouteChange();
        return result;
      };

      window.addEventListener("popstate", dispatchRouteChange);

      win.__apiErrorHistoryPatched = true;
    }
  }, []);

  const value = useMemo(
    () => ({
      hasApiError,
      resetApiError,
    }),
    [hasApiError, resetApiError]
  );

  return <ApiErrorContext.Provider value={value}>{children}</ApiErrorContext.Provider>;
};

export default ApiErrorContext;
